/**
 * AccessCertificateForm.js
 * "Access Certificate" form within the OC Submission tab.
 *
 * Recreates the NSW Installation Certificate — Access Certificate.
 * - Project Details: editable, empty by default; Project Name / Address sync
 *   from project.name / project.site_address on first load.
 * - Certification: fixed "Circulation Spaces" row with fixed text + standards reference.
 * - Installer Details: pre-filled with Revolead Construction defaults (editable), live signature, date.
 */

import { useState, useEffect, useRef } from 'react';
import SignaturePicker from '../../quote/SignaturePicker';
import { exportAccessCertificatePdf } from '../../../lib/accessCertificatePdfExport';

const NAVY = '#1a1a2e';
const GOLD = '#c9a84c';

const DEFAULTS = {
  project_name: '',
  address: '',
  cdc_no: '',
  lot: '',
  dp: '',
  building_class: '',
  date: '',

  installer_name: 'Liangliang Zheng',
  installer_company: 'Revolead Construction Pty Ltd',
  installer_licence: '383917C',
  installer_phone: '0452 229 888',
  installer_email: 'info@revoleadconstruction.com.au',
  installer_address: '27 George St, Clyde, NSW, 2142',
  signature: '',
};

const CERT_TEXT = 'Circulation spaces around partitions and doorways have been installed in accordance with the relevant standards and the approved documentation.';
const CERT_STANDARDS = 'BCA 2022 Amdt 2 — D4D2 · AS 1428.1:2021';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateLong(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ── Field treatment: invisible → dashed gold (hover) → solid gold + cream (focus) ──
const fieldClass =
  'w-full bg-transparent text-sm px-2 py-1 rounded-sm border border-transparent ' +
  'transition-colors outline-none ' +
  'hover:border hover:border-dashed hover:border-[#c9a84c] ' +
  'focus:border-solid focus:border-[#c9a84c] focus:bg-[#fdf8ee]';

function EditableInput({ value, onChange, mono, placeholder }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`${fieldClass} ${mono ? 'font-mono' : ''}`}
    />
  );
}

function EditableTextarea({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const grow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => { grow(ref.current); }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={2}
      className={`${fieldClass} resize-none overflow-hidden`}
    />
  );
}

function SectionBar({ title }) {
  return (
    <div
      className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest"
      style={{ background: GOLD, color: NAVY }}
    >
      {title}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <tr>
      <td
        className="align-top text-xs font-semibold p-3 w-1/3"
        style={{ color: NAVY, borderBottom: '1px solid #e5e4df' }}
      >
        {label}
      </td>
      <td className="align-top p-2" style={{ borderBottom: '1px solid #e5e4df' }}>
        {children}
      </td>
    </tr>
  );
}

export default function AccessCertificateForm({ project, data, onChange }) {
  const initialized = useRef(false);
  const [exporting, setExporting] = useState(false);

  // First-load sync: default date to today, sync project name / address from project
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const patch = {};
    if (!data.date) patch.date = todayISO();
    if (!data.project_name && (project?.name || project?.project_name)) {
      patch.project_name = project.name || project.project_name;
    }
    if (!data.address && project?.site_address) {
      patch.address = project.site_address;
    }
    if (Object.keys(patch).length > 0) {
      onChange({ ...data, ...patch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const d = { ...DEFAULTS, ...data };

  const set = (field, value) => onChange({ ...d, [field]: value });

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAccessCertificatePdf(d, project);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white border-t-4 shadow-sm" style={{ borderColor: NAVY }}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="p-6 sm:p-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
              Installation Certificate
            </p>
            <h2 className="text-2xl font-bold mt-1" style={{ color: NAVY, fontFamily: "'Space Grotesk', sans-serif" }}>
              Access Certificate
            </h2>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 rounded text-xs font-semibold disabled:opacity-50 transition-opacity"
            style={{ background: NAVY, color: GOLD }}
          >
            {exporting ? 'Generating…' : 'Export PDF'}
          </button>
        </div>
        <div className="h-0.5 mt-3 mb-0" style={{ background: GOLD }} />
      </div>

      {/* ── Project Details ──────────────────────────────────────────────── */}
      <SectionBar title="Project Details" />
      <table className="w-full border-collapse">
        <tbody>
          <Row label="Project Name">
            <EditableInput value={d.project_name} onChange={v => set('project_name', v)} />
          </Row>
          <Row label="Address">
            <EditableTextarea value={d.address} onChange={v => set('address', v)} />
          </Row>
          <Row label="CDC No.">
            <EditableInput mono value={d.cdc_no} onChange={v => set('cdc_no', v)} />
          </Row>
          <Row label="Lot">
            <EditableInput mono value={d.lot} onChange={v => set('lot', v)} />
          </Row>
          <Row label="DP">
            <EditableInput mono value={d.dp} onChange={v => set('dp', v)} />
          </Row>
          <Row label="Building Class">
            <EditableInput mono value={d.building_class} onChange={v => set('building_class', v)} />
          </Row>
          <Row label="Date">
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={d.date}
                onChange={e => set('date', e.target.value)}
                className={fieldClass}
                style={{ width: 160 }}
              />
              <span className="text-xs" style={{ color: '#6b7280' }}>{formatDateLong(d.date)}</span>
            </div>
          </Row>
        </tbody>
      </table>

      {/* ── Certification ────────────────────────────────────────────────── */}
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="text-left p-3 text-xs font-bold uppercase tracking-wider" style={{ background: NAVY, color: '#fff', width: '33%' }}>
              System Installed
            </th>
            <th className="text-left p-3 text-xs font-bold uppercase tracking-wider" style={{ background: NAVY, color: '#fff' }}>
              I certify that the following have been installed to:
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="align-top p-3 text-xs font-semibold" style={{ color: NAVY, borderBottom: '1px solid #e5e4df' }}>
              Circulation Spaces
            </td>
            <td className="align-top p-3 text-sm leading-relaxed" style={{ borderBottom: '1px solid #e5e4df', color: '#2c2c2c' }}>
              <p className="mb-2">{CERT_TEXT}</p>
              <p className="text-xs font-mono font-semibold" style={{ color: GOLD }}>{CERT_STANDARDS}</p>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ── Installer Details ────────────────────────────────────────────── */}
      <SectionBar title="Installer Details" />
      <table className="w-full border-collapse">
        <tbody>
          <Row label="Name">
            <EditableInput value={d.installer_name} onChange={v => set('installer_name', v)} />
          </Row>
          <Row label="Company">
            <EditableInput value={d.installer_company} onChange={v => set('installer_company', v)} />
          </Row>
          <Row label="Licence">
            <EditableInput mono value={d.installer_licence} onChange={v => set('installer_licence', v)} />
          </Row>
          <Row label="Phone">
            <EditableInput mono value={d.installer_phone} onChange={v => set('installer_phone', v)} />
          </Row>
          <Row label="Email">
            <EditableInput mono value={d.installer_email} onChange={v => set('installer_email', v)} />
          </Row>
          <Row label="Address">
            <EditableTextarea value={d.installer_address} onChange={v => set('installer_address', v)} />
          </Row>
          <Row label="Signature">
            <div className="max-w-sm">
              <SignaturePicker value={d.signature} onChange={v => set('signature', v)} />
            </div>
          </Row>
        </tbody>
      </table>

      <p className="text-xs px-3 py-3" style={{ color: '#9ca3af' }}>
        Changes are saved automatically.
      </p>
    </div>
  );
}
